import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight, Flame } from "lucide-react";
import { CategoryPill } from "./ArticleCard";

export default function HeroSlider({ articles = [] }) {
  const [i, setI] = useState(0);
  const items = articles.slice(0, 5);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setI((x) => (x + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) {
    return <div className="h-[460px] bg-zinc-100 rounded-3xl animate-pulse" />;
  }

  const a = items[i];

  return (
    <section className="relative" data-testid="hero-slider">
      <div className="grid lg:grid-cols-12 gap-4">
        {/* Big slider */}
        <div className="lg:col-span-8 relative overflow-hidden rounded-3xl h-[420px] sm:h-[500px] lg:h-[560px] shadow-xl bg-zinc-900">
          <AnimatePresence mode="wait">
            <motion.div
              key={a.id}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0"
            >
              {a.cover_image && (
                <img src={a.cover_image} alt={a.title} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            </motion.div>
          </AnimatePresence>

          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
            <div className="flex items-center gap-3 mb-3">
              <CategoryPill slug={a.category} />
              {a.is_breaking && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white text-[#E30613] text-[11px] font-outfit font-black uppercase tracking-widest">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#E30613] bodh-pulse" /> Breaking
                </span>
              )}
            </div>
            <h1 className="font-devanagari text-white font-black text-2xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
              {a.title}
            </h1>
            {a.subtitle && (
              <p className="mt-3 text-zinc-200 max-w-2xl font-devanagari text-sm sm:text-base">{a.subtitle}</p>
            )}
            <div className="mt-5 flex items-center gap-3">
              <Link
                to={`/article/${a.slug}`}
                data-testid="hero-readmore"
                className="inline-flex items-center gap-1.5 bg-white text-zinc-900 px-5 py-2.5 rounded-full font-outfit font-bold text-sm hover:bg-yellow-300 transition"
              >
                पूरी खबर पढें <ChevronRight className="h-4 w-4" />
              </Link>
              <span className="text-zinc-300 text-xs font-hind">{a.author}</span>
            </div>
          </div>

          {/* Dot indicators */}
          <div className="absolute top-5 right-5 flex gap-1.5">
            {items.map((_, j) => (
              <button
                key={j}
                data-testid={`hero-dot-${j}`}
                onClick={() => setI(j)}
                className={`h-1.5 rounded-full transition-all ${j === i ? "w-8 bg-white" : "w-3 bg-white/40"}`}
              />
            ))}
          </div>
        </div>

        {/* Side stack */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          {items.slice(1, 4).map((it) => (
            <Link
              to={`/article/${it.slug}`}
              key={it.id}
              data-testid={`hero-side-${it.id}`}
              className="bodh-card relative h-32 sm:h-[170px] overflow-hidden rounded-2xl group bg-zinc-100"
            >
              {it.cover_image && <img src={it.cover_image} alt={it.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700" />}
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
              <div className="absolute inset-0 p-4 flex flex-col justify-between">
                <CategoryPill slug={it.category} />
                <h3 className="font-devanagari text-white font-bold text-sm sm:text-base leading-snug line-clamp-3">{it.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
